import React, {useContext, useEffect, useState} from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    TextField,
    Alert,
} from '@mui/material';
import APIWrapperContext from '../../../../../services/APIWrapperContext';
import {
    InvoiceService,
    InvoiceEmitter,
    InvoiceEmitterCreate,
    InvoiceEmitterUpdate,
} from '../../../../../services/invoiceService';

interface EmitterDialogProps {
    open: boolean;
    emitter: InvoiceEmitter | null;
    onClose: () => void;
    onSave: () => void;
}

const EmitterDialog: React.FC<EmitterDialogProps> = ({open, emitter, onClose, onSave}) => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [validationAttempted, setValidationAttempted] = useState(false);

    // Form fields
    const [shortKey, setShortKey] = useState('');
    const [name, setName] = useState('');
    const [tradingName, setTradingName] = useState('');
    const [street, setStreet] = useState('');
    const [additionalStreet, setAdditionalStreet] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [city, setCity] = useState('');
    const [countryCode, setCountryCode] = useState('DE');
    const [vatId, setVatId] = useState('');
    const [taxNumber, setTaxNumber] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [iban, setIban] = useState('');
    const [bic, setBic] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountHolder, setAccountHolder] = useState('');
    const [contactName, setContactName] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    // Additional company info for PDF generation
    const [website, setWebsite] = useState('');
    const [fax, setFax] = useState('');
    const [management, setManagement] = useState('');
    const [courtRegistration, setCourtRegistration] = useState('');
    const [poBox, setPoBox] = useState('');
    const [logoPath, setLogoPath] = useState('');
    const [active, setActive] = useState(true);

    useEffect(() => {
        if (emitter) {
            setShortKey(emitter.short_key);
            setName(emitter.name);
            setTradingName(emitter.trading_name || '');
            setStreet(emitter.street);
            setAdditionalStreet(emitter.additional_street || '');
            setPostalCode(emitter.postal_code);
            setCity(emitter.city);
            setCountryCode(emitter.country_code);
            setVatId(emitter.vat_id || '');
            setTaxNumber(emitter.tax_number || '');
            setCompanyId(emitter.company_id || '');
            setIban(emitter.iban);
            setBic(emitter.bic || '');
            setBankName(emitter.bank_name || '');
            setAccountHolder(emitter.account_holder || '');
            setContactName(emitter.contact_name || '');
            setContactPhone(emitter.contact_phone || '');
            setContactEmail(emitter.contact_email || '');
            // Additional company info
            setWebsite(emitter.website || '');
            setFax(emitter.fax || '');
            setManagement(emitter.management || '');
            setCourtRegistration(emitter.court_registration || '');
            setPoBox(emitter.po_box || '');
            setLogoPath(emitter.logo_path || '');
            setActive(emitter.active);
        } else {
            // Reset form for new emitter
            setShortKey('');
            setName('');
            setTradingName('');
            setStreet('');
            setAdditionalStreet('');
            setPostalCode('');
            setCity('');
            setCountryCode('DE');
            setVatId('');
            setTaxNumber('');
            setCompanyId('');
            setIban('');
            setBic('');
            setBankName('');
            setAccountHolder('');
            setContactName('');
            setContactPhone('');
            setContactEmail('');
            // Additional company info
            setWebsite('');
            setFax('');
            setManagement('');
            setCourtRegistration('');
            setPoBox('');
            setLogoPath('');
            setActive(true);
        }
        setError(null);
        setValidationAttempted(false);
    }, [emitter, open]);

    // Helper to check if a required field has an error
    const hasError = (value: string) => validationAttempted && !value.trim();

    const handleSave = () => {
        setValidationAttempted(true);

        // Validation
        if (!shortKey.trim() || !name.trim() || !street.trim() ||
            !postalCode.trim() || !city.trim() || !iban.trim()) {
            setError('Bitte füllen Sie alle Pflichtfelder aus');
            return;
        }

        setSaving(true);
        setError(null);

        const data: InvoiceEmitterCreate | InvoiceEmitterUpdate = {
            short_key: shortKey.trim(),
            name: name.trim(),
            trading_name: tradingName.trim() || undefined,
            street: street.trim(),
            additional_street: additionalStreet.trim() || undefined,
            postal_code: postalCode.trim(),
            city: city.trim(),
            country_code: countryCode.trim(),
            vat_id: vatId.trim() || undefined,
            tax_number: taxNumber.trim() || undefined,
            company_id: companyId.trim() || undefined,
            iban: iban.trim(),
            bic: bic.trim() || undefined,
            bank_name: bankName.trim() || undefined,
            account_holder: accountHolder.trim() || undefined,
            contact_name: contactName.trim() || undefined,
            contact_phone: contactPhone.trim() || undefined,
            contact_email: contactEmail.trim() || undefined,
            // Additional company info for PDF generation
            website: website.trim() || undefined,
            fax: fax.trim() || undefined,
            management: management.trim() || undefined,
            court_registration: courtRegistration.trim() || undefined,
            po_box: poBox.trim() || undefined,
            logo_path: logoPath.trim() || undefined,
            active,
        };

        const apiCall = emitter
            ? InvoiceService.updateEmitter(emitter.id, data)
            : InvoiceService.createEmitter(data as InvoiceEmitterCreate);

        call_backend_api(
            apiCall,
            () => {
                setSaving(false);
                onSave();
            },
            (err: any) => {
                console.error('Error saving emitter:', err);
                setError('Fehler beim Speichern');
                setSaving(false);
            },
            undefined,
            false
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {emitter ? 'Rechnungsaussteller bearbeiten' : 'Neuen Rechnungsaussteller erstellen'}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{mb: 2, mt: 1}}>
                        {error}
                    </Alert>
                )}
                <Grid container spacing={2} sx={{mt: 0.5}}>
                    {/* Basic Info */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Kürzel"
                            value={shortKey}
                            onChange={(e) => setShortKey(e.target.value)}
                            fullWidth
                            required
                            error={hasError(shortKey)}
                            placeholder="z.B. bau"
                        />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            required
                            error={hasError(name)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Handelsname"
                            value={tradingName}
                            onChange={(e) => setTradingName(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Address */}
                    <Grid item xs={12}>
                        <TextField
                            label="Straße"
                            value={street}
                            onChange={(e) => setStreet(e.target.value)}
                            fullWidth
                            required
                            error={hasError(street)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Adresszusatz"
                            value={additionalStreet}
                            onChange={(e) => setAdditionalStreet(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="PLZ"
                            value={postalCode}
                            onChange={(e) => setPostalCode(e.target.value)}
                            fullWidth
                            required
                            error={hasError(postalCode)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField
                            label="Stadt"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            fullWidth
                            required
                            error={hasError(city)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Land"
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Tax Info */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="USt-IdNr."
                            value={vatId}
                            onChange={(e) => setVatId(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Steuernummer"
                            value={taxNumber}
                            onChange={(e) => setTaxNumber(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Handelsregisternr."
                            value={companyId}
                            onChange={(e) => setCompanyId(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Bank Info */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="IBAN"
                            value={iban}
                            onChange={(e) => setIban(e.target.value)}
                            fullWidth
                            required
                            error={hasError(iban)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="BIC"
                            value={bic}
                            onChange={(e) => setBic(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Bankname"
                            value={bankName}
                            onChange={(e) => setBankName(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Kontoinhaber"
                            value={accountHolder}
                            onChange={(e) => setAccountHolder(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Contact Info */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Kontaktperson"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Telefon"
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="E-Mail"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Additional Company Info for PDF */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Website"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            fullWidth
                            placeholder="www.example.de"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Fax"
                            value={fax}
                            onChange={(e) => setFax(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Geschäftsführung"
                            value={management}
                            onChange={(e) => setManagement(e.target.value)}
                            fullWidth
                            placeholder="Max Mustermann, Erika Musterfrau"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Amtsgericht / HRB"
                            value={courtRegistration}
                            onChange={(e) => setCourtRegistration(e.target.value)}
                            fullWidth
                            placeholder="Amtsgericht Musterstadt HRB 12345"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Postfach"
                            value={poBox}
                            onChange={(e) => setPoBox(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Logo-Pfad"
                            value={logoPath}
                            onChange={(e) => setLogoPath(e.target.value)}
                            fullWidth
                            placeholder="/path/to/logo.png"
                        />
                    </Grid>

                    {/* Active Status */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={active}
                                    onChange={(e) => setActive(e.target.checked)}
                                />
                            }
                            label="Aktiv"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Abbrechen
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? 'Speichern...' : 'Speichern'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmitterDialog;
